import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CasesService } from 'src/app/services/cases/cases.service';
import { PaymentStepForm, ProductType, productTypes, Summary } from 'src/app/services/form/form.resources';
import { FormService } from 'src/app/services/form/form.service';
import { StartWithTransferDialogComponent, StartWithTransferDialogData } from './start-with-transfer-dialog/start-with-transfer-dialog.component';
import { SUMMARY_FORM_NAME } from 'src/app/services/form/form.factory';

@Component({
  selector: 'app-summary-form',
  templateUrl: './summary-form.component.html',
  styleUrls: ['./summary-form.component.scss']
})
export class SummaryFormComponent implements OnInit, OnDestroy {
  readonly paymentMethod = this.fb.control('card');
  get form(): FormGroup { return this.formService.form.get(SUMMARY_FORM_NAME) as FormGroup; }
  get summary(): Summary { return this.formService.forms.find(({ name }) => name === 'summary')?.data as Summary; }

  get productType(): FormControl { return this.form.get('product_type') as FormControl; }
  get debtorEmail(): FormControl { return this.form.get('debtor.email') as FormControl; }
  get isStartCasePriceClaimAdded(): FormControl { return this.form.get('payee_case.is_start_case_price_claim_added') as FormControl; }
  get isInterestAdded(): FormControl { return this.form.get('payee_case.is_interest_added') as FormControl; }
  get isFlatRateCostClaimAdded(): FormControl { return this.form.get('payee_case.is_flat_rate_cost_claim_added') as FormControl; }

  get loading(): boolean { return this.formService.saving.value > 0; }

  private readonly optionalClaimForms: PaymentStepForm[] = [
    'flat_rate_cost_added_form',
    'interest_added_form',
    'start_case_price_added_form',
  ];

  get showProductForms(): boolean { return this.summary.product.is_product_type_editable; }
  get productForms(): PaymentStepForm[] { return this.summary.product.forms[this.summary.product.type]; }
  get showOptionalClaimsForm(): boolean { return this.productForms.some(form => this.optionalClaimForms.includes(form)); };
  get showDebtorForm(): boolean { return this.productForms.includes('debtor_form'); };
  get showFlatRateCostAddedForm(): boolean {
    return this.summary.payee_case.has_claims_eligible_for_flat_rate_cost
      && this.productForms.includes('flat_rate_cost_added_form');
  };
  get showInterestAddedForm(): boolean { return this.productForms.includes('interest_added_form'); };
  get showStartCasePriceAddedForm(): boolean { return this.productForms.includes('start_case_price_added_form'); };
  get showAllClaimsAmount(): boolean {
    return (!this.isFlatRateCostClaimAdded.value || this.summary.payee_case.flat_rate_cost_claims_sum !== null)
    && (!this.isInterestAdded.value || this.summary.payee_case.interest_amount_claims_sum !== null);
  }

  get flatRateCostAmount(): string {
    return this.summary.payee_case.flat_rate_cost_claims_sum?.toLocaleString('hu-HU');
  }

  get interestAmount(): string | null {
    if (!this.summary.payee_case.interest_amount_claims_sum) {
      return null;
    }

    // Format all currencies in the interest amount sums
    const formattedAmounts = Object.entries(this.summary.payee_case.interest_amount_claims_sum)
      .map(([currency, sum]) => `${sum.remaining_amount.toLocaleString('hu-HU')} ${currency}`)
      .join(' | ');

    return formattedAmounts;
  }

  get startCaseFeeAmount(): number | null {
    if (this.summary.fees.start.some(fee => fee.amount === null)) {
      return null;
    }

    return this.summary.fees.start.reduce((sum, fee) => sum + fee.amount, 0);
  }
  get successCaseFeeAmount(): number {
    if (this.summary.fees['closed-success'].some(fee => fee.amount === null)) {
      return 0;
    }

    return this.summary.fees['closed-success'].reduce((sum, fee) => sum + fee.amount, 0);
  }

  private readonly destroy = new Subject<void>();

  hasUnsavedChanges = false;

  showProduct: Record<ProductType, boolean> = {
    notice: false,
    demand_letter: false,
    hard_1: false,
    hard_2: false,
  };

  constructor(
    private formService: FormService,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private casesService: CasesService,
  ) {
    for (const type of productTypes) {
      this.showProduct[type] = this.summary.active_products.includes(type);
    }
  }

  async ngOnInit() {
    this.formService.saving
      .pipe(takeUntil(this.destroy))
      .subscribe({
        next: value => {
          if (value <= 0) {
            this.hasUnsavedChanges = false;
          }
        }
      });

    this.form.valueChanges
      .pipe(
        takeUntil(this.destroy),
      )
      .subscribe({
        next: () => {
          this.hasUnsavedChanges = true;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  onInterestToggle(event: boolean): void {
    this.isInterestAdded.patchValue(event);
  }

  onFlatRateCostClaimToggle(event: boolean): void {
    this.isFlatRateCostClaimAdded.patchValue(event);
  }

  async startWithUnknownPayment(): Promise<void> {
    const result = await this.casesService.startWithUnknownFees(this.formService.case.id);
    this.formService.redirectToCases(result.payee_case_reference_id);
  }

  async startInstantly(): Promise<void> {
    await this.casesService.startSoftSuccessCase(this.formService.case.id);
    this.formService.redirectAfterCaseStarted();
  }

  async startCaseWithPayment(): Promise<void> {
    if (this.paymentMethod.value === 'card') {
      try {
        await this.formService.startWithCard();
      } catch (error) {
        console.error('Error while starting with card', error);
      }
    } else if (this.paymentMethod.value === 'transfer') {
      try {
        await this.formService.startWithTransfer();
        this.openCaseStartSuccessDialog();
      } catch (error) {
        console.error('Error while starting with transfer', error);
      }
    }
  }

  private openCaseStartSuccessDialog() {
    const data: StartWithTransferDialogData = {
      currency_iso: this.summary.product.currency_iso,
      payee_case_reference_id: this.formService.case.payee_case_reference_id,
      transfer_amount: this.startCaseFeeAmount,
    };
    const dialogRef = this.dialog.open(StartWithTransferDialogComponent, {
      data,
      position: {
        top: '50px',
      },
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe(async () => {
      this.formService.redirectToCases();
    });
  }
}
